import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Tooltip } from 'antd';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import {
    Button,
    EmptyState,
    ErrorState,
    FormField,
    Input,
    Modal,
    SkeletonTable,
    useToast,
} from '@/components/ui';
import {
    createConsoleRole,
    fetchConsoleRoles,
    updateConsoleRole,
    type ConsoleRole,
    type PermissionCatalog,
} from '@/api/orgAdmin';
import { AssignRoleDrawer } from './AssignRoleDrawer';
import {
    CaretIcon,
    CheckIcon,
    FilterIcon,
    InfoIcon,
    LockIcon,
    MinusIcon,
    SearchIcon,
    WarnIcon,
} from './icons';
import styles from './Permissions.module.css';

export interface RolesTabProps {
    catalog: UseQueryResult<PermissionCatalog>;
}

/**
 * EAA-1843: "Vai trò" — zip-11 frame 63.
 *
 * Split panel: pick a role on the left, tick what it may do on the right. The
 * grid on tab 1 edits every role at once; this screen is the one-role view, with
 * room for the two things a matrix cell has nowhere to put — the built-in
 * comparison columns and the plain-language "Việc được làm".
 *
 * DRAFT STATE IS LOCAL UNTIL SAVED. The checkboxes mutate a local set and the
 * save bar reports the delta; nothing writes per click. Two reasons: the frame
 * shows an explicit "Lưu vai trò" with an undo, and autosaving a permission set
 * means a mis-click briefly grants real authority to everyone holding the role.
 *
 * Built-ins render locked rather than hidden. An operator composing a custom
 * role against "what org-admin can do" needs both visible, and the API refuses
 * to edit them anyway — hiding them would only move that discovery to a 403.
 */
export function RolesTab({ catalog }: RolesTabProps) {
    const { t } = useTranslation();
    const can = useCan();
    const toast = useToast();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const canManage = can('console.roles.manage');

    const rolesQuery = useQuery({
        queryKey: ['console-roles', accessToken],
        queryFn: () => fetchConsoleRoles(config.apiBase, accessToken as string),
        enabled: Boolean(accessToken),
        retry: false,
    });

    const builtin = useMemo(() => rolesQuery.data?.builtin ?? [], [rolesQuery.data]);
    const custom = useMemo(() => rolesQuery.data?.custom ?? [], [rolesQuery.data]);

    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [draft, setDraft] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState('');
    const [group, setGroup] = useState<string | null>(null);
    const [onlySelected, setOnlySelected] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [justCreated, setJustCreated] = useState<string | null>(null);
    const [assignFor, setAssignFor] = useState<ConsoleRole | null>(null);

    const selected: ConsoleRole | null = useMemo(() => {
        const all = [...builtin, ...custom];
        return all.find((r) => (r.id ?? r.code) === selectedKey) ?? all[0] ?? null;
    }, [builtin, custom, selectedKey]);

    // Reset the draft whenever the selection changes OR the server copy is
    // refetched, so a save elsewhere never leaves a stale draft looking dirty.
    useEffect(() => {
        setDraft(new Set(selected?.permissions ?? []));
    }, [selected?.id, selected?.code, selected?.permissions]);

    // Memoized: a bare `?? []` mints a fresh array every render, so every memo
    // below would recompute on each keystroke instead of only when data changes.
    const permissions = useMemo(() => catalog.data?.permissions ?? [], [catalog.data]);
    const groups = useMemo(() => catalog.data?.groups ?? [], [catalog.data]);
    const catalogRoles = useMemo(() => catalog.data?.roles ?? [], [catalog.data]);

    const countByGroup = useMemo(() => {
        const counts = new Map<string, number>();
        for (const p of permissions) counts.set(p.group, (counts.get(p.group) ?? 0) + 1);
        return counts;
    }, [permissions]);

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        return permissions.filter((p) => {
            if (group && p.group !== group) return false;
            if (onlySelected && !draft.has(p.code)) return false;
            if (!term) return true;
            return p.code.toLowerCase().includes(term) || p.label.toLowerCase().includes(term);
        });
    }, [permissions, search, group, onlySelected, draft]);

    const byGroup = useMemo(() => {
        const map = new Map<string, typeof permissions>();
        for (const p of visible) map.set(p.group, [...(map.get(p.group) ?? []), p]);
        // Grouped by stable KEY, labelled with the translated name.
        return groups
            .filter((g) => map.has(g.key))
            .map((g) => [g.key, g.label, map.get(g.key)!] as const);
    }, [visible, groups]);

    const dirty = useMemo(() => {
        const server = new Set(selected?.permissions ?? []);
        if (server.size !== draft.size) return true;
        for (const c of draft) if (!server.has(c)) return true;
        return false;
    }, [draft, selected]);

    const changeCount = useMemo(() => {
        const server = new Set(selected?.permissions ?? []);
        let n = 0;
        for (const c of draft) if (!server.has(c)) n += 1;
        for (const c of server) if (!draft.has(c)) n += 1;
        return n;
    }, [draft, selected]);

    const editable =
        canManage && Boolean(selected) && !selected?.is_builtin && Boolean(selected?.is_active);

    const saveRole = useMutation({
        mutationFn: () =>
            updateConsoleRole(config.apiBase, accessToken as string, selected!.id as string, {
                permissions: [...draft],
            }),
        onSuccess: (role) => {
            toast.success(t('console.permissions.roles.savedRoleToast', { name: role.name }));
            void queryClient.invalidateQueries({ queryKey: ['console-roles', accessToken] });
        },
        onError: (e: unknown) =>
            toast.error(
                e instanceof Error ? e.message : t('console.permissions.roles.saveErrorToast')
            ),
    });

    function toggle(code: string) {
        if (!editable) return;
        setDraft((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code);
            else next.add(code);
            return next;
        });
    }

    function toggleMany(codes: string[], allOn: boolean) {
        if (!editable) return;
        setDraft((prev) => {
            const next = new Set(prev);
            for (const c of codes) {
                if (allOn) next.delete(c);
                else next.add(c);
            }
            return next;
        });
    }

    if (rolesQuery.isLoading) return <SkeletonTable rows={6} columns={2} />;
    if (rolesQuery.isError) {
        return (
            <ErrorState
                description={t('console.permissions.roles.loadErrorDescription')}
                onRetry={() => void rolesQuery.refetch()}
                retrying={rolesQuery.isFetching}
            />
        );
    }

    const allVisible = visible.map((p) => p.code);
    const allOn = allVisible.length > 0 && allVisible.every((c) => draft.has(c));
    const filterCount = (group ? 1 : 0) + (onlySelected ? 1 : 0) + (search.trim() ? 1 : 0);

    return (
        <div className={styles.rl}>
            <div className={styles.rlLayout}>
                <aside className={styles.rlRail}>
                    <p className={styles.rlRailHead}>
                        {t('console.permissions.roles.builtinSectionHeading')}
                    </p>
                    {builtin.map((r) => (
                        <RoleItem
                            key={r.code}
                            role={r}
                            active={selected?.code === r.code && !selected?.id}
                            fresh={false}
                            onSelect={() => setSelectedKey(r.code)}
                        />
                    ))}

                    <span className={styles.rlRailRule} aria-hidden="true" />
                    <p className={styles.rlRailHead}>
                        {t('console.permissions.roles.customSectionHeading')}
                    </p>
                    {custom.length === 0 && (
                        <p className={styles.rlRailEmpty}>
                            {t('console.permissions.roles.noCustomRoles')}
                        </p>
                    )}
                    {custom.map((r) => (
                        <RoleItem
                            key={r.id ?? r.code}
                            role={r}
                            active={selected?.id === r.id}
                            fresh={r.id === justCreated}
                            editing={r.id === selected?.id && dirty}
                            onSelect={() => setSelectedKey(r.id as string)}
                        />
                    ))}

                    {canManage && (
                        <>
                            <button
                                type="button"
                                className={styles.rlAdd}
                                onClick={() => setCreateOpen(true)}
                            >
                                <span aria-hidden="true">+</span>
                                {t('console.permissions.roles.addRoleButton')}
                            </button>
                            <p className={styles.rlRailHint}>
                                {t('console.permissions.roles.addRoleHint')}
                            </p>
                        </>
                    )}
                </aside>

                <section className={styles.rlPane}>
                    {!selected && (
                        <EmptyState title={t('console.permissions.roles.selectRolePrompt')} />
                    )}
                    {selected && (
                        <>
                            <header className={styles.rlPaneHead}>
                                <span className={styles.rlPaneTitles}>
                                    <span className={styles.rlPaneTitle}>{selected.name}</span>
                                    <span className={styles.rlPaneSub}>
                                        {t('console.permissions.roles.selectedCountPrefix')}{' '}
                                        <strong className={styles.rlPaneCount}>{draft.size}</strong>{' '}
                                        {t('console.permissions.roles.selectedCountSuffix', {
                                            total: permissions.length,
                                        })}
                                        {selected.is_builtin
                                            ? ` ${t('console.permissions.roles.builtinSourceDefined')}`
                                            : ` ${t('console.permissions.roles.assignedCount', {
                                                  count: selected.assignment_count ?? 0,
                                              })}`}
                                        {/* The anti-escalation rule used to be a
                                            permanent amber band above the whole
                                            tab. It is a caveat about ONE action —
                                            ticking a box — so it now sits on the
                                            line that counts the ticks, one hover
                                            away, instead of ~70px of banner on
                                            every visit. */}
                                        {editable && (
                                            <Tooltip
                                                title={t(
                                                    'console.permissions.roles.antiEscalationTooltip'
                                                )}
                                                placement="bottom"
                                            >
                                                <button
                                                    type="button"
                                                    className={styles.rlWhy}
                                                    aria-label={t(
                                                        'console.permissions.roles.whyLockedAriaLabel'
                                                    )}
                                                >
                                                    <InfoIcon size={13} />
                                                </button>
                                            </Tooltip>
                                        )}
                                    </span>
                                </span>
                                {canManage && !selected.is_builtin && (
                                    <button
                                        type="button"
                                        className={styles.rlAssign}
                                        onClick={() => setAssignFor(selected)}
                                    >
                                        {t('console.permissions.roles.assignButton')}
                                    </button>
                                )}
                            </header>

                            <div className={styles.pmToolbar}>
                                <div className={styles.pmToolbarRow}>
                                    {/* EAA-1804: kit `Input` (borderless, since `pmSearch`/
                                        `rlSearch` already paint the border/radius/background)
                                        instead of a hand-rolled label + raw input wrapping a
                                        manual `SearchIcon`.
                                        EAA-1846: the inner input is styled through antd's own
                                        `input` slot; the old `.pm-search input` rule reached
                                        every descendant input, this one reaches the field. */}
                                    <Input
                                        className={`${styles.pmSearch} ${styles.rlSearch}`}
                                        classNames={{ input: styles.pmSearchInput }}
                                        variant="borderless"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder={t(
                                            'console.permissions.roles.searchPlaceholder'
                                        )}
                                        aria-label={t('console.permissions.roles.searchAriaLabel')}
                                        allowClear
                                        prefix={<SearchIcon />}
                                    />
                                    <span className={styles.pmSpacer} />
                                    <button
                                        type="button"
                                        className={
                                            onlySelected
                                                ? `${styles.pmPill} ${styles.pmPillOn}`
                                                : styles.pmPill
                                        }
                                        aria-pressed={onlySelected}
                                        onClick={() => setOnlySelected((v) => !v)}
                                    >
                                        {t('console.permissions.roles.onlySelectedToggle')}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.pmPill}
                                        onClick={() => {
                                            setSearch('');
                                            setGroup(null);
                                            setOnlySelected(false);
                                        }}
                                        disabled={filterCount === 0}
                                    >
                                        <FilterIcon />
                                        {t('console.permissions.roles.filterButton', {
                                            count: filterCount,
                                        })}
                                    </button>
                                    <span className={styles.pmMeta} aria-live="polite">
                                        {t('console.permissions.roles.countDisplay', {
                                            visible: visible.length,
                                            total: permissions.length,
                                        })}
                                    </span>
                                </div>
                                <div
                                    className="perm-chips"
                                    role="group"
                                    aria-label={t('console.permissions.roles.groupFilterAriaLabel')}
                                >
                                    <button
                                        type="button"
                                        className={
                                            group === null
                                                ? `${styles.permChip} ${styles.permChipOn}`
                                                : styles.permChip
                                        }
                                        aria-pressed={group === null}
                                        onClick={() => setGroup(null)}
                                    >
                                        {t('console.permissions.roles.allChipLabel')}{' '}
                                        <span className={styles.permChipN}>
                                            {permissions.length}
                                        </span>
                                    </button>
                                    {groups.map((g) => (
                                        <button
                                            key={g.key}
                                            type="button"
                                            className={
                                                group === g.key
                                                    ? `${styles.permChip} ${styles.permChipOn}`
                                                    : styles.permChip
                                            }
                                            aria-pressed={group === g.key}
                                            onClick={() => setGroup(group === g.key ? null : g.key)}
                                        >
                                            {g.label}{' '}
                                            <span className={styles.permChipN}>
                                                {countByGroup.get(g.key) ?? 0}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className={styles.rlScroll}>
                                <table className={styles.rlTable}>
                                    <thead>
                                        <tr>
                                            <th
                                                scope="col"
                                                className={`${styles.rlTh} ${styles.rlThBox}`}
                                            >
                                                <Box
                                                    state={allOn ? 'on' : 'off'}
                                                    disabled={!editable}
                                                    label={t(
                                                        'console.permissions.roles.selectAllVisibleLabel'
                                                    )}
                                                    onToggle={() => toggleMany(allVisible, allOn)}
                                                />
                                            </th>
                                            <th
                                                scope="col"
                                                className={`${styles.rlTh} ${styles.rlThCode}`}
                                            >
                                                {t('console.permissions.roles.columnCode')}
                                                <CaretIcon
                                                    size={13}
                                                    className={styles.rlThCodeCaret}
                                                />
                                            </th>
                                            <th scope="col" className={styles.rlTh}>
                                                {t('console.permissions.roles.columnLabel')}
                                            </th>
                                            {catalogRoles.map((r) => (
                                                <th
                                                    key={r.key}
                                                    scope="col"
                                                    className={`${styles.rlTh} ${styles.rlThCmp}`}
                                                >
                                                    {r.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {byGroup.map(([groupKey, groupLabel, rowsInGroup]) => {
                                            const codes = rowsInGroup.map((r) => r.code);
                                            const on = codes.filter((c) => draft.has(c)).length;
                                            return (
                                                <Fragment key={groupKey}>
                                                    <tr>
                                                        <td
                                                            colSpan={3 + catalogRoles.length}
                                                            className={`${styles.rlTd} ${styles.rlGroupCell}`}
                                                        >
                                                            <div className={styles.rlGroup}>
                                                                <Box
                                                                    state={
                                                                        on === 0
                                                                            ? 'off'
                                                                            : on === codes.length
                                                                              ? 'on'
                                                                              : 'partial'
                                                                    }
                                                                    disabled={!editable}
                                                                    label={t(
                                                                        'console.permissions.roles.toggleGroupLabel',
                                                                        { group: groupLabel }
                                                                    )}
                                                                    onToggle={() =>
                                                                        toggleMany(
                                                                            codes,
                                                                            on === codes.length
                                                                        )
                                                                    }
                                                                />
                                                                <span
                                                                    className={styles.rlGroupName}
                                                                >
                                                                    {groupLabel}
                                                                </span>
                                                                <span className={styles.rlGroupN}>
                                                                    {on}/{codes.length}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {rowsInGroup.map((p) => {
                                                        const isOn = draft.has(p.code);
                                                        return (
                                                            <tr
                                                                key={p.code}
                                                                className={
                                                                    isOn ? styles.rlRowOn : ''
                                                                }
                                                            >
                                                                <td
                                                                    className={`${styles.rlTd} ${styles.rlTdBox}`}
                                                                >
                                                                    <Box
                                                                        state={isOn ? 'on' : 'off'}
                                                                        disabled={!editable}
                                                                        label={p.label}
                                                                        onToggle={() =>
                                                                            toggle(p.code)
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className={styles.rlTd}>
                                                                    <code className={styles.rlCode}>
                                                                        {p.code}
                                                                    </code>
                                                                    {p.writes_externally && (
                                                                        <span
                                                                            className={styles.rlExt}
                                                                            title={t(
                                                                                'console.permissions.roles.writesExternallyTooltip'
                                                                            )}
                                                                        >
                                                                            {t(
                                                                                'console.permissions.roles.writesExternallyBadge'
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td
                                                                    className={
                                                                        isOn
                                                                            ? `${styles.rlTd} ${styles.rlLabel} ${styles.rlLabelOn}`
                                                                            : `${styles.rlTd} ${styles.rlLabel}`
                                                                    }
                                                                >
                                                                    {p.label}
                                                                </td>
                                                                {catalogRoles.map((r) => (
                                                                    <td
                                                                        key={r.key}
                                                                        className={`${styles.rlTd} ${styles.rlCmp}`}
                                                                    >
                                                                        {r.permissions.includes(
                                                                            p.code
                                                                        ) ? (
                                                                            <span
                                                                                className={
                                                                                    styles.rlCmpYes
                                                                                }
                                                                                role="img"
                                                                                aria-label={t(
                                                                                    'console.permissions.roles.yesAriaLabel'
                                                                                )}
                                                                            >
                                                                                <CheckIcon
                                                                                    size={15}
                                                                                />
                                                                            </span>
                                                                        ) : (
                                                                            <span
                                                                                className={
                                                                                    styles.rlCmpNo
                                                                                }
                                                                                role="img"
                                                                                aria-label={t(
                                                                                    'console.permissions.roles.noAriaLabel'
                                                                                )}
                                                                            >
                                                                                <MinusIcon
                                                                                    size={15}
                                                                                />
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        );
                                                    })}
                                                </Fragment>
                                            );
                                        })}
                                        {byGroup.length === 0 && (
                                            <tr>
                                                <td
                                                    colSpan={3 + catalogRoles.length}
                                                    className={`${styles.rlTd} ${styles.permEmpty}`}
                                                >
                                                    {t('console.permissions.roles.noMatchMessage')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {editable && (
                                <div className={styles.pmSavebar}>
                                    <span className={dirty ? styles.pmDirty : styles.pmClean}>
                                        {dirty && <WarnIcon />}
                                        {dirty
                                            ? t('console.permissions.roles.dirtyChanges', {
                                                  count: changeCount,
                                              })
                                            : t('console.permissions.roles.noChangesLabel')}
                                    </span>
                                    <span className={styles.pmSpacer} />
                                    {/* EAA-1804: kit `Button` instead of hand-written
                                        `<button className="portal-btn">` — see the mapping
                                        note on CreateRoleModal below. */}
                                    <Button
                                        className={styles.pmBtn}
                                        disabled={!dirty || saveRole.isPending}
                                        onClick={() => setDraft(new Set(selected.permissions))}
                                    >
                                        {t('console.permissions.roles.undoButton')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        className={styles.pmBtn}
                                        disabled={!dirty || saveRole.isPending}
                                        onClick={() => saveRole.mutate()}
                                    >
                                        {saveRole.isPending
                                            ? t('console.permissions.roles.savingPending')
                                            : t('console.permissions.roles.saveRoleButton')}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </section>
            </div>

            <CreateRoleModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(role) => {
                    setCreateOpen(false);
                    setSelectedKey(role.id as string);
                    setJustCreated(role.id);
                    void queryClient.invalidateQueries({
                        queryKey: ['console-roles', accessToken],
                    });
                }}
            />

            <AssignRoleDrawer role={assignFor} onClose={() => setAssignFor(null)} />
        </div>
    );
}

/**
 * Frame 63's rail line: "Chỉ đọc · 40 quyền · 3 người". The status prefix is the
 * part that earns its place — "Đang sửa" tells you which role your unsaved edits
 * belong to once the pane has scrolled away from the rail.
 *
 * We report `lượt gán` where the frame says `người`: the API counts assignments,
 * and one assignment can be a group of forty. A number labelled "người" that is
 * really a grant count is the kind of thing an operator finds wrong later.
 *
 * EAA-1845: takes `t` as a parameter — this is a plain function, not a
 * component/hook, so the caller (a component) supplies its own `t`.
 */
function railMeta(role: ConsoleRole, editing: boolean, t: TFunction): string {
    const n = role.permissions.length;
    const perms =
        n === 0
            ? t('console.permissions.roles.noPermissionsLabel')
            : t('console.permissions.roles.permCount', { count: n });
    let status: string;
    if (role.is_builtin) status = t('console.permissions.roles.statusReadOnly');
    else if (!role.is_active) status = t('console.permissions.roles.statusInactive');
    else if (editing) status = t('console.permissions.roles.statusEditing');
    else status = t('console.permissions.roles.statusActive');
    // An empty role is reported by its emptiness — "Đang dùng · Chưa có quyền
    // nào" reads as a contradiction, and the frame writes exactly that case as
    // "Chưa có quyền nào · 0 người".
    const bare = n === 0 && !role.is_builtin && role.is_active && !editing;
    const head = bare ? perms : `${status} · ${perms}`;
    return role.is_builtin
        ? head
        : `${head} ${t('console.permissions.roles.railAssignmentSuffix', {
              count: role.assignment_count ?? 0,
          })}`;
}

function RoleItem({
    role,
    active,
    fresh,
    editing = false,
    onSelect,
}: {
    role: ConsoleRole;
    active: boolean;
    fresh: boolean;
    editing?: boolean;
    onSelect: () => void;
}) {
    const { t } = useTranslation();
    return (
        <button
            type="button"
            className={active ? `${styles.rlItem} ${styles.rlItemOn}` : styles.rlItem}
            onClick={onSelect}
        >
            <span className={styles.rlItemBody}>
                <span className={styles.rlItemName}>
                    {role.name}
                    {fresh && (
                        <span className={styles.rlFresh}>
                            {t('console.permissions.roles.freshBadge')}
                        </span>
                    )}
                </span>
                <span className={styles.rlItemMeta}>{railMeta(role, editing, t)}</span>
            </span>
            {role.is_builtin && <LockIcon size={15} className={styles.rlItemLock} />}
        </button>
    );
}

interface BoxProps {
    state: 'on' | 'off' | 'partial';
    disabled: boolean;
    label: string;
    onToggle: () => void;
}

/** Frame 63's 17px tick box — the same three states as the matrix, one size up. */
function Box({ state, disabled, label, onToggle }: BoxProps) {
    return (
        <button
            type="button"
            className={state === 'off' ? styles.rlBox : `${styles.rlBox} ${styles.rlBoxOn}`}
            disabled={disabled}
            aria-pressed={state === 'on'}
            aria-label={label}
            onClick={onToggle}
        >
            {state === 'on' && <CheckIcon size={12} />}
            {state === 'partial' && <MinusIcon size={12} />}
        </button>
    );
}

function CreateRoleModal({
    open,
    onClose,
    onCreated,
}: {
    open: boolean;
    onClose: () => void;
    onCreated: (r: ConsoleRole) => void;
}) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const toast = useToast();
    const [name, setName] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);

    const create = useMutation({
        mutationFn: () =>
            createConsoleRole(config.apiBase, accessToken as string, {
                name: name.trim(),
                code: code.trim(),
                // Created empty on purpose: the permission set is composed in the
                // editor, where the whole catalog is visible with its descriptions.
                permissions: [],
            }),
        onSuccess: (role) => {
            toast.success(t('console.permissions.roles.createdRoleToast', { name: role.name }));
            setName('');
            setCode('');
            onCreated(role);
        },
        onError: (e: unknown) =>
            setError(
                e instanceof Error ? e.message : t('console.permissions.roles.createErrorToast')
            ),
    });

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={t('console.permissions.roles.addRoleButton')}
            footer={
                <div className="hz-move-footer">
                    <span className="hz-move-footer-note">
                        {t('console.permissions.roles.createFooterNote')}
                    </span>
                    <span className="hz-move-footer-actions">
                        {/* EAA-1804: kit `Button` instead of hand-written
                            `<button className="portal-btn">` — the antd theme already
                            carries this component sheet (controlHeight, cta palette,
                            fontWeight — see packages/ui/src/theme/antdTheme.ts), so the
                            swap is props-for-classes: portal-btn -> default,
                            -primary -> type="primary". */}
                        <Button onClick={onClose}>{t('common.cancel')}</Button>
                        <Button
                            type="primary"
                            disabled={!name.trim() || !code.trim() || create.isPending}
                            onClick={() => {
                                setError(null);
                                create.mutate();
                            }}
                        >
                            {create.isPending
                                ? t('console.permissions.roles.creatingPending')
                                : t('console.permissions.roles.createButton')}
                        </Button>
                    </span>
                </div>
            }
        >
            <div className="portal-admin-form">
                {/* EAA-1804: kit `FormField` + `Input` instead of a hand-rolled
                    `<label>` + `<span className="portal-field-label">` pair. */}
                <FormField label={t('console.permissions.roles.fieldNameLabel')}>
                    <Input
                        value={name}
                        placeholder={t('console.permissions.roles.fieldNamePlaceholder')}
                        onChange={(e) => {
                            setName(e.target.value);
                            setCode(slugify(e.target.value));
                        }}
                    />
                </FormField>
                {/* EAA-1804: REAL BUG fix — this field used to render an
                    unconditional hint `<p>` AND, whenever `error` was set, a second
                    `<p>` stacked below it, showing both lines at once. The root
                    CLAUDE.md rule draws exactly one line under a control — a hint,
                    or an error REPLACING it. `FormField`'s `hint`/`error` props
                    enforce that by construction (see `FormField.tsx`). */}
                <FormField
                    label={t('console.permissions.roles.fieldCodeLabel')}
                    hint={t('console.permissions.roles.fieldCodeHint')}
                    error={error}
                >
                    <Input
                        className="hz-code-input"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                    />
                </FormField>
            </div>
        </Modal>
    );
}

/** Local copy — `orgText.slugify` lives in the hierarchy folder and is not shared. */
function slugify(value: string): string {
    return value
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export default RolesTab;
